'use client';

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRBAC } from '@/contexts/rbac-context';
import { useApi } from '@/hooks/useApi';
import { APPROVAL_MENU_KEY, APPROVAL_ENTRY_MENU_KEYS, approvalClient } from '@/lib/approval-client';
import type { ApprovalRequestSummary, ApprovalSummary } from '@/types/approval';
import { ContractPriceChange, formatNumber, PriceDelta, requestItemLabel, requestDataValue } from '@/components/features/approval/approval-common';
import { ExternalLink, Stamp } from 'lucide-react';

const PENDING_PREVIEW_SIZE = 3;

const TILES = [
  { key: 'pendingMine', label: '내 결재 대기', box: 'border-foreground/30 bg-accent' },
  { key: 'myInProgress', label: '상신 진행중' },
  { key: 'myCompleted', label: '상신 완결', recent: true },
  { key: 'myClosed', label: '반려 · 취소', recent: true, num: 'text-destructive' },
] as const;

/** 결재 새 창 열기. 대시보드는 탭 쉘 안이라 SPA 는 새 창으로 띄운다 */
function useOpenApprovals() {
  const { orgCode } = useParams();
  return (path: string) => window.open(`/${orgCode}/approvals${path}`, '_blank', 'noopener,noreferrer');
}

/**
 * 대시보드 전자결재 문서현황. 기본은 접힌 상태이고, 접혀 있어도 내 차례 건수는 배지로 보여준다.
 * 진입 메뉴가 없으면 아예 렌더링하지 않는다 - 건수 조회도 하지 않는다.
 */
export function ApprovalStatusCard() {
  const { canAccessMenu } = useRBAC();
  if (!APPROVAL_ENTRY_MENU_KEYS.some(canAccessMenu)) return null;
  return <ApprovalStatus />;
}

function ApprovalStatus() {
  const { canAccessMenu } = useRBAC();
  const openApprovals = useOpenApprovals();
  const { data, loading, error, refetch } = useApi<ApprovalSummary>(() => approvalClient.summary(), []);

  // 대시보드 새로고침 버튼은 이벤트로 전파된다 (다른 카드들과 같은 방식)
  useEffect(() => {
    window.addEventListener('dashboard-refresh', refetch);
    return () => window.removeEventListener('dashboard-refresh', refetch);
  }, [refetch]);

  // 결재함 메뉴가 있는 프로그램만 조회한다 - 없는 프로그램을 부르면 403 이다
  const inboxKeys = ['a330', 'a340'].filter(canAccessMenu);

  return (
    <Card className='px-3'>
      <Accordion type='single' collapsible>
        <AccordionItem value='approval' className='border-b-0'>
          <AccordionTrigger className='py-3 hover:no-underline'>
            <span className='flex items-center gap-2 text-sm font-medium'>
              <Stamp className='size-4 text-primary' />
              전자결재 문서현황
              {!!data?.pendingMine && <Badge>내 차례 {data.pendingMine}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent className='pb-3'>
            {loading ? (
              <Skeleton className='h-20 w-full' />
            ) : error || !data ? (
              <p className='py-2 text-xs text-muted-foreground'>문서현황을 불러오지 못했습니다.</p>
            ) : (
              <div className='space-y-3'>
                <div className='grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2'>
                  {TILES.map(t => (
                    <div key={t.key} className={`rounded-lg border px-3 py-2 ${'box' in t ? t.box : ''}`}>
                      <div className='flex items-baseline gap-1.5'>
                        <span className={`text-2xl font-semibold tabular-nums leading-none ${'num' in t ? t.num : ''}`}>
                          {formatNumber(data[t.key])}
                        </span>
                        {'recent' in t && <span className='text-[11px] text-muted-foreground'>{data.recentDays}일</span>}
                      </div>
                      <p className='mt-1 break-keep text-xs font-medium'>{t.label}</p>
                    </div>
                  ))}
                </div>

                {inboxKeys.length > 0 && <PendingList menuKeys={inboxKeys} onOpen={openApprovals} />}

                <button
                  type='button'
                  onClick={() => openApprovals('')}
                  className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
                >
                  전자결재 열기
                  <ExternalLink className='size-3' />
                </button>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

/** 아코디언이 닫히면 Radix 가 언마운트하므로, 펼칠 때만 이 컴포넌트가 뜨고 목록을 부른다 */
function PendingList({ menuKeys, onOpen }: { menuKeys: string[]; onOpen: (path: string) => void }) {
  const { data, loading, error } = useApi<ApprovalRequestSummary[]>(() => fetchPending(menuKeys), []);

  return (
    <div>
      <p className='pb-1 text-xs text-muted-foreground'>내 차례 문서</p>
      {loading ? (
        <Skeleton className='h-16 w-full' />
      ) : error ? (
        <p className='rounded-lg border py-3 text-center text-xs text-muted-foreground'>목록을 불러오지 못했습니다</p>
      ) : !data?.length ? (
        // 건수는 프로그램을 안 가리고 이 목록은 내가 가진 결재함만 본다 - "없다"고 단정하지 않는다
        <p className='rounded-lg border py-3 text-center text-xs text-muted-foreground'>
          내 결재함에 대기 중인 문서가 없습니다
        </p>
      ) : (
        <ul className='divide-y rounded-lg border'>
          {data.map(r => (
            <li key={r.id}>
              <button
                type='button'
                onClick={() => onOpen(`/${r.id}`)}
                className='block w-full px-3 py-2 text-left hover:bg-accent'
              >
                <div className='flex items-start justify-between gap-2'>
                  <span className='break-keep text-sm font-medium'>{r.orgName ?? r.orgCd ?? '-'}</span>
                  <span className='shrink-0 text-[11px] text-muted-foreground'>
                    {requestDataValue(r.requestData, 'applyDate')} 적용
                  </span>
                </div>
                <div className='flex items-center justify-between gap-2 text-xs text-muted-foreground'>
                  <span className='break-keep'>{requestItemLabel(r.requestData)}</span>
                  <span className='shrink-0 text-foreground'>
                    {r.programId === 'DEALER_PRICE' ? (
                      <ContractPriceChange data={r.requestData} />
                    ) : (
                      <PriceDelta current={r.requestData?.currentPrice} requested={r.requestData?.requestedPrice} />
                    )}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 결재함 메뉴를 가진 프로그램만 돌며 내 차례 건을 모은다. 미승인·취소미승인 둘 다 지금 내 차례인 건만
 * 나오므로(전체설계 §9.4) 두 조회구분을 합쳐야 `/me/approvals/summary` 의 pendingMine 과 숫자가 맞는다
 */
async function fetchPending(menuKeys: string[]): Promise<ApprovalRequestSummary[]> {
  const pages = await Promise.all(
    menuKeys.flatMap(key => {
      const { box, program } = APPROVAL_MENU_KEY[key];
      return (['UNAPPROVED', 'CANCEL_UNAPPROVED'] as const).map(view =>
        approvalClient.list(box, program, { view, size: PENDING_PREVIEW_SIZE })
      );
    })
  );
  return pages
    .flatMap(p => p.content)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, PENDING_PREVIEW_SIZE);
}
